import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { publicAxios } from '@/lib/axios';
import { isKnownRoute } from '@/lib/knownRoutes';
import { consumeAppHandoffParams, restoreAppReferrerPolicy } from '@/lib/auth/appHandoffParams';
import { lookupBrowserSessionToken } from '@/lib/auth/lookupBrowserSessionToken';

const replaceLocation = (path: string) => window.location.replace(path);

const AppHandoff = ({ redirect = replaceLocation }: { redirect?: (path: string) => void }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const pending = useRef<Promise<{ token: string; redirectTo: string }> | null>(null);
  const previousToken = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Reuse the same exchange during StrictMode's effect replay. The code is single-use.
    if (!pending.current) {
      const params = consumeAppHandoffParams(location.search);
      previousToken.current = localStorage.getItem('token');
      pending.current = (async () => {
        const code = params.get('code');
        if (!code) throw new Error('missing_code');
        const { data } = await publicAxios.post('/api/app-handoff/exchange', { code });
        const redirectTo = data?.redirectTo;
        if (
          typeof redirectTo !== 'string' || !redirectTo.startsWith('/') ||
          redirectTo.startsWith('//') || /[\\\s]/.test(redirectTo) ||
          [...redirectTo].some(character => character.charCodeAt(0) < 32) ||
          !isKnownRoute(redirectTo.split(/[?#]/)[0])
        ) throw new Error('invalid_redirect');
        if (typeof data?.jwt !== 'string' || !data.jwt) throw new Error('missing_session');
        const token = await lookupBrowserSessionToken(data.jwt);
        if (!token) throw new Error('invalid_session');
        return { token, redirectTo };
      })();
    }

    pending.current.then(({ token, redirectTo }) => {
      if (cancelled) return;
      restoreAppReferrerPolicy();
      if (localStorage.getItem('token') !== previousToken.current) {
        navigate('/', { replace: true });
        return;
      }
      localStorage.setItem('token', token);
      // Reload so the auth provider and all account-scoped clients start with this session.
      redirect(redirectTo);
    }).catch(() => {
      if (cancelled) return;
      restoreAppReferrerPolicy();
      if (localStorage.getItem('token') !== previousToken.current) {
        navigate('/', { replace: true });
        return;
      }
      toast({ title: t('auth.authError'), description: t('auth.authProcessingError'), variant: 'destructive' });
      navigate('/login', { replace: true });
    });
    return () => { cancelled = true; };
  }, [location.search, navigate, redirect, t, toast]);

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-800 border-gray-700" role="status">
        <CardHeader className="text-center">
          <img src="/images/autoclipper-logo-full.png" alt="Autoclipper" className="h-12 w-auto mx-auto mb-4" />
          <CardTitle className="text-2xl text-white">{t('auth.processingLogin')}</CardTitle>
          <CardDescription className="text-gray-400">{t('auth.processingAuthWait')}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
};

export default AppHandoff;
